import { useCallback } from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'

import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  itemNames: Array<string>
}

function ConfirmDeleteTripListItemsModal({ itemNames }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<boolean>()
  const itemCount = itemNames.length
  const itemName = itemNames[0] ?? ''

  const handleClose = useCallback(() => {
    modalElement.resolve(false)
  }, [modalElement])

  const handleConfirm = useCallback(() => {
    modalElement.resolve(true)
  }, [modalElement])

  const title = itemCount === 1
    ? intl.formatMessage({
        id: 'oFlZDgddigLJogML4CiT',
        defaultMessage: 'Delete item?',
        description: 'Title for the modal confirming one selected trip planner list row delete',
      })
    : intl.formatMessage({
        id: 'hN0sJ_pB-PcND471nUrC',
        defaultMessage: 'Delete selected items?',
        description: 'Title for the modal confirming multiple selected trip planner list row deletes',
      })
  const message = itemCount === 1
    ? intl.formatMessage(
        {
          id: 'imhXcIL57ItygLyBtxHU',
          defaultMessage: 'This will remove <bold>{itemName}</bold> from your itinerary.',
          description: 'Confirmation message when deleting one selected row from the trip planner list view',
        },
        {
          itemName,
          bold: (chunks: ReactNode) => <b>{chunks}</b>,
        },
      )
    : intl.formatMessage(
        {
          id: 'ysbeGXIvJT22W71OpXGO',
          defaultMessage: 'This will remove {itemCount} selected items from your itinerary. Any existing bookings will not be cancelled.',
          description: 'Confirmation message when deleting multiple selected rows from the trip planner list view',
        },
        { itemCount },
      )
  const deleteLabel = intl.formatMessage({
    id: 'spt0KyJjY4Z2J8vsZiYa',
    defaultMessage: 'Delete',
    description: 'Primary action button label confirming selected trip planner list rows should be deleted',
  })
  const cancelLabel = intl.formatMessage({
    id: 'PnYzpOcChfM5YZHVcVvs',
    defaultMessage: 'Cancel',
    description: 'Secondary action button label cancelling selected trip planner list row deletion',
  })

  return (
    <Modal
      title={title}
      primaryActionText={deleteLabel}
      secondaryActionText={cancelLabel}
      onPrimaryActionClick={handleConfirm}
      onSecondaryActionClick={handleClose}
      onClose={handleClose}
      tabletHeight="auto"
    >
      <BodyText variant="medium">
        {message}
      </BodyText>
    </Modal>
  )
}

export default ConfirmDeleteTripListItemsModal
